import {
	ReactElement,
	useMemo,
	useState
} from "react";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import {
	convertArbitraryValueForTrackingId,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import classNames from "classnames";
import styles from "./SegmentedControl.module.less";

export type SegmentedControlItem<TValue> = {
	text: string;
	value: TValue;
	activeColor?: SegmentedControlActiveColor;
	valueTrackingId?: string;
	counter?: number;
};

export type SegmentedControlProps<TValue> = {
	value: TValue | undefined;
	onChange(value: TValue): void;
	options: readonly SegmentedControlItem<TValue>[];
	trackingId: string;
};

export const SegmentedControl = <TValue,>(
	props: SegmentedControlProps<TValue>
): ReactElement => {
	const {
		value,
		onChange,
		options,
		trackingId
	} = props;

	const [groupName] = useState<Guid>(() => newGuid());

	const changeHandler = (isEnabled: boolean, value: TValue): void => {
		if (!isEnabled) {
			return;
		}
		onChange(value);
	};

	const activeIndex = useMemo(
		(): number => {
			return options.findIndex((x) => x.value === value);
		},
		[options, value]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
			style={{
				["--_segment-control-count"]: options.length,
				["--_segment-control-active-index"]: activeIndex
			}}
		>
			<ul className={styles.list}>
				{options.map((item, index): ReactElement => (
					<li key={index} className={styles.listItem}>
						<label
							{...setTrackingId(trackingId, `|${getValueTrackingId(item)}`)}
							className={styles.segment}
							title={item.text}
						>
							<input
								className={styles.input}
								type="radio"
								checked={index === activeIndex}
								name={groupName}
								onChange={(args) => changeHandler(args.target.checked, item.value)}
							/>

							<span
								className={classNames(
									styles.text,
									activeColorToStyleMap[item.activeColor ?? "default"]
								)}
							>
								{item.text}
							</span>

							{isNotNil(item.counter) && (
								<span className={styles.counter}>
									{item.counter}
								</span>
							)}
						</label>
					</li>
				))}

				<li
					className={
						classNames(
							styles.indicator,
							{
								[styles.indicator_visible]: activeIndex !== -1
							}
						)
					}
				/>
			</ul>
		</div>
	);
};
SegmentedControl.displayName = "SegmentedControl";

export type SegmentedControlActiveColor = "default" | "success" | "danger";

const activeColorToStyleMap: Record<SegmentedControlActiveColor, string | undefined> = {
	"default": undefined,
	"success": styles.text_success,
	"danger": styles.text_danger
};

function getValueTrackingId<TValue>(item: SegmentedControlItem<TValue>): string {
	if (isNotNilOrEmpty(item.valueTrackingId)) {
		return item.valueTrackingId;
	}

	return convertArbitraryValueForTrackingId(item.value);
};
